import FroalaCellPopup from '@/components/FroalaCellPopup.vue';
import FroalaTablePopup from '@/components/FroalaTablePopup.vue';
import {Popup} from '@/library/popup';
import {uniqueId} from '@/library/string';
import TopMessageHandler from '@/library/topMessageHandler';
import {type FroalaPlaceholder} from '@/typings/froala';
import FroalaEditor, {type FroalaOptions, type ToolbarButtons} from 'froala-editor';
import axios from 'axios';
import Tribute from 'tributejs';

export const FroalaToolbarSmall = (): Partial<ToolbarButtons> => ({
    moreMisc: {
        align: 'left',
        buttons: [
            'bold', 'italic', 'underline', 'strikeThrough', '|', 'subscript', 'superscript', '|', 'clearFormatting', '|', 'insertLink', '|', 'emoticons'
        ],
        buttonsVisible: 99,
    }
});

export const FroalaToolbarRegular = (): Partial<ToolbarButtons> => ({
    moreMisc: {
        align: 'left',
        buttons: [
            'bold', 'italic', 'underline', 'strikeThrough', '|', 'clearFormatting', '|', 'formatOL', 'formatUL', '|', 'insertLink'
        ],
        buttonsVisible: 99,
    }
});

export const FroalaToolbarSimple = (): Partial<ToolbarButtons> => ({
    moreMisc: {
        align: 'left',
        buttons: [
            'bold', 'italic', 'underline', 'strikeThrough', '|', 'undo', 'redo', '|', 'clearFormatting'
        ],
        buttonsVisible: 99,
    }
});

export const FroalaToolbarBasicText = (): Partial<ToolbarButtons> => ({
    moreMisc: {
        align: 'left',
        buttons: [
            'textColor', 'backgroundColor', '|', 'formatOL', 'formatUL', 'outdent', 'indent', '|', 'quote',
            '-', // line break
            'bold', 'italic', 'underline', 'strikeThrough', '|', 'subscript', 'superscript', '|', 'clearFormatting'
        ],
        buttonsVisible: 99,
    }
});

function handlePreventEnter(event: KeyboardEvent)
{
    const handle = event.key === 'Enter' && !event.shiftKey && !event.ctrlKey;

    if (handle)
    {
        event.preventDefault();
        event.stopPropagation();
    }

    return !handle;
}

export const froalaPlaceholderTemplate = '<span id="[UID]" class="fr-widget-placeholder fr-deletable" contenteditable="false" draggable="true" data-placeholder-key="[PLACEHOLDER_VALUE]" data-placeholder-label="[PLACEHOLDER_LABEL]">'
    + '<span class="fr-widget-element">[&lrm;[&lrm;[PLACEHOLDER_LABEL]&lrm;]&lrm;]</span>'
    + '</span>';

export function froalaAddPlaceholder(placeholders: FroalaPlaceholder[])
{
    const placeholderSelect = {};
    for (let placeholder of placeholders)
    {
        placeholderSelect[placeholder.key] = placeholder.label;
    }

    FroalaEditor.DefineIcon('insertPlaceholder', {NAME: 'tags', SVG_KEY: 'tags'});
    FroalaEditor.RegisterCommand('insertPlaceholder', {
        title: 'Platzhalter einfügen',
        type: 'dropdown',
        options: placeholderSelect,
        undo: true,
        focus: true,
        refreshAfterCallback: true,
        callback: function (cmd, val, params) {
            this.html.insert(`[[${val}]]`);
            initPlaceholders(this);
        }
    });
}

export function initEditor()
{
    FroalaEditor.DefineIcon('linkOpenBlank', {NAME: 'openLink', SVG_KEY: 'openLink'});
    FroalaEditor.RegisterCommand('linkOpenBlank', {
        title: 'Link öffnen',
        undo: false,
        focus: true,
        refreshAfterCallback: false,
        callback: function () {
            const element = this.selection.element() as HTMLAnchorElement;
            window.open(element.href);
        }
    });

    FroalaEditor.DefineIcon('tableEdit', {NAME: 'tableStyle', SVG_KEY: 'tableStyle'});
    FroalaEditor.RegisterCommand('tableEdit', {
        title: 'Tabelleneigenschaften',
        undo: false,
        focus: true,
        refreshAfterCallback: false,
        callback: function () {
            const selectedCells: HTMLTableCellElement[] = Array.from(this.el.querySelectorAll('.fr-selected-cell'));
            const selectedTables                        = selectedCells.map(element => element.closest('table'))
            .filter((element, index, array) => array.indexOf(element) === index) as HTMLTableElement[];

            new Popup({
                title: 'Tabelleneigenschaften',
                titleIcon: 'table_view',
                size: 'small',
                component: FroalaTablePopup,
                componentProps: {
                    editor: this,
                    tables: selectedTables,
                },
            }).open();
        }
    });

    FroalaEditor.DefineIcon('tableCellEdit', {NAME: 'magic', SVG_KEY: 'cellStyle'});
    FroalaEditor.RegisterCommand('tableCellEdit', {
        title: 'Zelleneigenschaften',
        undo: false,
        focus: true,
        refreshAfterCallback: false,
        callback: function () {
            const cells = Array.from(this.el.querySelectorAll('.fr-selected-cell'));

            new Popup({
                title: 'Zelleneigenschaften',
                titleIcon: 'table_view',
                size: 'small',
                component: FroalaCellPopup,
                componentProps: {
                    editor: this,
                    cells,
                },
            }).open();
        }
    });

    FroalaEditor.DefineIcon("customTableRows",{NAME:"bars",SVG_KEY:"row"});
    FroalaEditor.RegisterCommand(
        'customTableRows',
        {
            type: 'dropdown',
            focus: !1,
            title: 'Row',
            options: {
                above: 'Insert row above',
                below: 'Insert row below',
                delete: 'Delete row',
                copy: 'Zeile kopieren',
            },
            html: function()
            {
                let template = '<ul class="fr-dropdown-list" role="presentation">';
                // @ts-ignore (provided by plugin, missing in global froala type)
                const options = FroalaEditor.COMMANDS.customTableRows.options;

                for (const option in options)
                {
                    if (!options.hasOwnProperty(option))
                    {
                        continue;
                    }

                    (template += '<li role="presentation"><a class="fr-command" tabIndex="-1" role="option" data-cmd="customTableRows" data-param1="'
                        + option
                        + '" title="'
                        + this.language.translate(options[option])
                        + '">'
                        + this.language.translate(options[option])
                        + '</a></li>');
                }

                template += '</ul>';

                return template;
            },
            callback: function(_, command)
            {
                if ('copy' === command)
                {
                    const selectedCells: HTMLTableCellElement[] = Array.from(this.el.querySelectorAll('.fr-selected-cell'));
                    const selectedRows                          = selectedCells.map((element: HTMLTableCellElement) => element.closest('tr'))
                    .filter((element, index, array) => array.indexOf(element) === index) as HTMLTableRowElement[];

                    let hasSelection = true;

                    const selection = this.selection.get() as Selection;
                    const focusNode = selection.focusNode as Nullable<HTMLElement>;
                    if (focusNode && focusNode.classList && focusNode.classList.contains('fr-element'))
                    {
                        hasSelection = false;
                    }

                    if (!this.undo.canDo())
                    {
                        this.undo.saveStep();
                    }

                    hasSelection && this.selection.save();

                    for (const element of selectedRows)
                    {
                        const clonedElement = element.cloneNode(true) as HTMLTableRowElement;

                        for (const clonedElementSelectedChild of clonedElement.querySelectorAll('.fr-selected-cell'))
                        {
                            clonedElementSelectedChild.classList.contains('fr-selected-cell') && clonedElementSelectedChild.classList.remove('fr-selected-cell');
                        }

                        element.insertAdjacentElement('afterend', clonedElement);
                    }

                    hasSelection && this.selection.restore();

                    this.undo.saveStep();

                    return;
                }

                'above' == command || 'below' == command ? this.table.insertRow(command) : this.table.deleteRow();
            }
        }
    );
}

let clickPlaceholder: Nullable<HTMLElement> = null;
let skipPlaceholderInit = false;

export function initPlaceholders(instance: FroalaEditor)
{
    let hasSelection = true;

    const selection = instance.selection.get() as Selection;
    const focusNode = selection.focusNode as Nullable<HTMLElement>;
    if (focusNode && focusNode.classList && focusNode.classList.contains('fr-element'))
    {
        hasSelection = false;
    }

    hasSelection && instance.selection.save();

    let html = instance.html.get(true);
    const match = html.match(/\[\[(\w*)\]\]/g);

    if (match)
    {
        for (let _match of match)
        {
            const key = _match.replace(/(\[|\])/g, '');
            const label      = instance.opts.placeholders[key];

            const uid      = uniqueId();
            const template = froalaPlaceholderTemplate.replace('[UID]', uid).replace('[PLACEHOLDER_VALUE]', key).replace(/\[PLACEHOLDER_LABEL\]/g, label);

            html = html.replace(new RegExp(`[[${key}]]`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')), template);
        }
    }

    skipPlaceholderInit = true;
    instance.html.set(html);
    skipPlaceholderInit = false;

    hasSelection && instance.selection.restore();

    for (const placeholder of instance.$el[0].querySelectorAll('.fr-widget-placeholder'))
    {
        placeholder.addEventListener('dragstart', function (event: Event) {
            event.stopPropagation();
            const dataTransfer = (event as DragEvent).dataTransfer as DataTransfer;
            dataTransfer.setData('id', placeholder.id);
            dataTransfer.setData('value', placeholder.attributes.getNamedItem('data-placeholder-key')!.value);
            dataTransfer.setData('label', placeholder.attributes.getNamedItem('data-placeholder-label')!.value);
        });

        placeholder.addEventListener('click', function(event: MouseEvent) {
            clickPlaceholder = placeholder;
            if (!instance.popups.get('portunaPlaceholder.popup'))
            {
                Object.assign(FroalaEditor.POPUP_TEMPLATES, {
                    'portunaPlaceholder.popup': '[_BUTTONS_]'
                });

                FroalaEditor.RegisterCommand('removePlaceholder', {
                    title: 'Entfernen',
                    icon: 'remove',
                    undo: true,
                    focus: true,
                    refreshAfterCallback: true,
                    callback: function () {
                        clickPlaceholder && clickPlaceholder.remove();
                        instance.popups.hide('portunaPlaceholder.popup');
                    }
                });

                // Popup buttons.
                let popupButtons = '<div class="fr-buttons">';
                popupButtons += instance.button.buildList(['removePlaceholder']);
                popupButtons += '</div>';

                instance.popups.create('portunaPlaceholder.popup', {
                    buttons: popupButtons,
                });
            }

            function getOffset(element: HTMLElement)
            {
                if (!element.getClientRects().length)
                {
                    return { top: 0, left: 0 };
                }

                let rect = element.getBoundingClientRect();
                let win = element.ownerDocument.defaultView;
                return ({
                    top: rect.top + (win ? win.pageYOffset : 0),
                    left: rect.left + (win ? win.pageXOffset : 0)
                });
            }

            instance.popups.setContainer('portunaPlaceholder.popup', instance.$tb);

            // Trigger refresh for the popup.
            instance.popups.refresh('portunaPlaceholder.popup');

            // This custom popup is opened by pressing a button from the editor's toolbar.
            // Get the button's object in order to place the popup relative to it.
            const button = event.target as HTMLSpanElement;
            const buttonOffset = getOffset(button);

            const left = buttonOffset.left - 28 + button.offsetWidth / 2;
            const top = buttonOffset.top + 15;

            instance.popups.show('portunaPlaceholder.popup', left, top, button.offsetWidth);
        });
    }
}

export const FroalaDefaultConfig: Partial<FroalaOptions> = {
    attribution: false,
    events: {
        initialized: function() {
            const editor = this;

            if (editor.opts.loadMentions)
            {
                axios.get(editor.opts.mentionsFetchUrl || '/application/ajax/fetch-mention-users').then(({data: users}) => {
                    if (editor._tribute)
                    {
                        return;
                    }

                    const tribute = new Tribute({
                        values: users.map((entry: any) => ({value: entry.id, key: entry.name + ' ' + entry.surname})),
                        selectTemplate: function(item) {
                            // @ts-ignore
                            return item.original.key;
                        }
                    });

                    tribute.attach(editor.el);

                    editor.events.on('keydown', function(event: KeyboardEvent) {
                        if (event.key === 'Enter' && tribute.isActive) {
                            // @ts-ignore
                            tribute.isActiveDebounce = true;
                            window.setTimeout(() => {
                                // @ts-ignore
                                tribute.isActiveDebounce = false;
                            }, 200);
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        }

                        if (editor.opts.preventEnter && !handlePreventEnter(event))
                        {
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        }

                        return;
                    }, true);

                    editor.events.on('keyup', function(event: KeyboardEvent) {
                        // @ts-ignore
                        if (event.key === 'Enter' && tribute.isActiveDebounce) {
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        }

                        return;
                    }, true);

                    editor._tribute = tribute;
                });
            }

            if (editor.opts.preventEnter && !editor.opts.loadMentions)
            {
                editor.events.on('keydown', handlePreventEnter, true);
            }

            if (editor.opts.placeholders)
            {
                initPlaceholders(this);

                editor.events.on('commands.redo', () => initPlaceholders(this));
                editor.events.on('commands.undo', () => initPlaceholders(this));

                editor.events.on('paste.after', function()
                {
                    //<editor-fold desc="fix placeholder after paste">
                    for (const element of editor.$el.find('[data-placeholder-key]'))
                    {
                        element.replaceWith(document.createTextNode(`[[${element.getAttribute('data-placeholder-key')}]] `));
                    }

                    let html    = editor.html.get(true);
                    const match = html.match(/\[\u200e\[\u200e(\w*)\u200e\]\u200e\]/g);

                    if (match)
                    {
                        let replaced: string[] = [];

                        for (let _match of match)
                        {
                            const label = _match.replace(/([\[\]\u200e])/g, '');
                            if (replaced.includes(label))
                            {
                                continue;
                            }

                            const labelIndex = Object.values(editor.opts.placeholders).indexOf(label);
                            if (-1 === labelIndex)
                            {
                                replaced.push(label);
                                continue;
                            }

                            const key = Object.keys(editor.opts.placeholders)[labelIndex];

                            html = html.replace(new RegExp(_match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), `[[${key}]] `);
                            replaced.push(label);
                        }

                        skipPlaceholderInit = true;
                        editor.html.set(html);
                        skipPlaceholderInit = false;
                    }

                    initPlaceholders(editor);
                    //</editor-fold>
                });

                editor.events.on('drop', function (dropEvent) {
                    function getContentWrapper(element: Node|HTMLElement): Nullable<Node>
                    {
                        if ('attributes' in element && element.attributes.getNamedItem('contenteditable')?.value === 'false')
                        {
                            return element;
                        }

                        if (element?.parentNode)
                        {
                            return getContentWrapper(element.parentNode)
                        }

                        return null;
                    }

                    // Focus at the current position.
                    editor.markers.insertAtPoint(dropEvent.originalEvent);

                    const $marker = editor.$el.find('.fr-marker');
                    $marker.replaceWith(FroalaEditor.MARKERS);
                    editor.selection.restore();

                    // TODO: avoid drop into contenteditable false
                    const selection = editor.selection.get() as Selection;
                    const selectionFocusNode  = getContentWrapper(selection.focusNode as HTMLElement);

                    if (selectionFocusNode)
                    {
                        const ghostNode = document.createElement('span');
                        selectionFocusNode.parentNode?.appendChild(ghostNode);
                        editor.selection.setAfter(ghostNode, true);
                        // Focus at the current position.
                        editor.markers.insert();
                        editor.selection.restore();
                    }

                    // Save into undo stack the current position.
                    if (!editor.undo.canDo())
                    {
                        editor.undo.saveStep();
                    }

                    // Insert HTML.
                    const value = dropEvent.originalEvent.dataTransfer.getData('value');
                    const label = dropEvent.originalEvent.dataTransfer.getData('label');
                    if (value && label) {
                        const id = dropEvent.originalEvent.dataTransfer.getData('id');
                        id && document.querySelector('#' + id)!.remove();

                        editor.html.insert(`[[${value}]]&nbsp;`);

                        initPlaceholders(editor);
                    }

                    // Save into undo stack the changes.
                    editor.undo.saveStep();

                    // Stop event propagation.
                    dropEvent.preventDefault();
                    dropEvent.stopPropagation();

                    // Firefox show cursor.
                    if (editor.core.hasFocus() && editor.browser.mozilla) {
                        editor.events.disableBlur();
                        setTimeout(function () {
                            editor.$el.blur().focus();
                            editor.events.enableBlur();
                        }, 0);
                    }

                    return false;
                }, true);
            }

            if (editor.opts.initializationCallbacks)
            {
                for (let callback of editor.opts.initializationCallbacks)
                {
                    callback(editor);
                }
            }
        },
        'image.beforeUpload': function(files: Blob[]) {
            const editor = this;

            if (editor.opts.imageUpload && files.length) {
                // Create a File Reader.
                const reader = new FileReader();
                // Set the reader to insert images when they are loaded.
                reader.onload = function(event: ProgressEvent<FileReader>) {
                    const result = event.target!.result as string;
                    editor.image.insert(result, false, {}, editor.image.get());
                };
                // Read image as base64.
                reader.readAsDataURL(files[0]);
            }

            if (!editor.opts.imageUpload)
            {
                editor.image.remove(editor.image.get());
                TopMessageHandler.info('Das Hochladen von lokalen Bilddateien ist in diesem Editor nicht gestattet.');
            }

            editor.popups.hideAll();
            // Stop default upload chain.
            return false;
        },
        'html.set': function () {
            if (this.opts.placeholders)
            {
                !skipPlaceholderInit && initPlaceholders(this);
            }
        },
        // @ts-ignore (wrong typing + documentation, return value is used for editor.html.get())
        'html.get': function (html: string) {
            return html.replace(/id="isPasted"/g,'');
        },
        'paste.before': function()
        {
            //@ts-ignore
            this.opts.linkAlwaysBlank = true;

            return true;
        },
        'paste.after': function()
        {
            //@ts-ignore
            this.opts.linkAlwaysBlank = false;

            return true;
        },
        destroy: function () {
            const editor = this;

            if (editor._tribute)
            {
                editor._tribute.detach(editor.el);
                delete editor._tribute;
            }
        }
    },
    enter: FroalaEditor.ENTER_DIV,
    emoticonsUseImage: false,
    fontFamilySelection: true,
    fontSizeSelection: true,
    heightMin: 300,
    htmlRemoveTags: ['style', 'link'],
    imageUpload: false,
    imageUploadRemoteUrls: false,
    imagePaste: true,
    initializationCallbacks: [],
    key: '1CC3kA6B4F4A4D4E3I3cWHNGGDTCWHId1Eb1Oc1Yh1b2Ld1POkE3D3F3C7A4A4A3G3H3G2==',
    language: 'de',
    linkEditButtons: ['linkOpenBlank', 'linkStyle', 'linkEdit', 'linkRemove'],
    linkList: [],
    linkText: true,
    paragraphFormatSelection: true,
    pluginsEnabled: ['align', 'codeView', 'colors', 'draggable', 'emoticons', 'fontFamily', 'fontSize', 'image', 'link', 'lists', 'paragraphFormat', 'paragraphStyle', 'quote', 'table', 'url', 'video', 'wordPaste'],
    spellcheck: true,
    toolbarButtons: {
        moreMisc: {
            buttons: [
                'insertTable', 'insertHR', '|', 'formatOL', 'formatUL', 'outdent', 'indent', '|', 'quote', '|', 'insertImage', 'insertVideo', 'html',
                '-', // line break
                'bold', 'italic', 'underline', 'strikeThrough', '|', 'subscript', 'superscript', '|', 'clearFormatting', '|', 'alignLeft', 'alignCenter', 'alignRight', 'alignJustify', '|', 'undo', 'redo',
                '-', // line break
                'paragraphFormat', 'fontFamily', 'fontSize', '|', 'textColor', 'backgroundColor', '|', 'insertLink'
            ],
            buttonsVisible: 99,
        }
    },
    tableStyles: {
        'fr-dashed-borders': 'Gestrichelte Rahmen',
        'fr-alternate-rows': 'Abwechselnde Zeilen',
        'fr-portuna-table-no-border': 'Rahmenlos',
        'fr-portuna-table-full-width': 'Volle Breite erzwingen',
        'fr-portuna-table-half-width': 'Halbe Breite erzwingen',
    },
    tableEditButtons: [
        'tableHeader',
        'tableFooter',
        'tableRemove',
        'customTableRows',
        'tableColumns',
        'tableEdit',
        '-',
        'tableCells',
        'tableCellBackground',
        'tableCellVerticalAlign',
        'tableCellHorizontalAlign',
        'tableCellEdit',
    ],
    toolbarStickyOffset: 80,
    videoUpload: false,
};

export const FroalaInlineDefaultConfig = (): Partial<FroalaOptions> => Object.assign({}, FroalaDefaultConfig, {
    toolbarButtons: FroalaToolbarSmall(),
});